"use client"

import { useState } from "react"

import { Button, toast } from "@coval/ui"
import { ThumbsDown, ThumbsUp } from "lucide-react"

import { TRACKING_EVENTS, useAnalytics } from "@/lib/analytics"

type Rating = "positive" | "negative"

/**
 * Hover-revealed thumbs up/down for a single completed Sofia assistant reply.
 * This is the only feedback control on Sofia's messages, page summaries
 * included — they arrive as ordinary assistant messages, so they are rated the
 * same way as anything else she says. The legacy `surface: "covi"` preserves
 * dashboards; `assistant_brand: "sofia"` is the canonical post-rename
 * dimension.
 *
 * One vote per message: clicking either thumb records it and removes the
 * control for that reply, so a reply can't be re-voted or spam-clicked. The
 * vote is final once cast — there is no toggle-off or change-vote path.
 *
 * `session_id` groups votes within one panel session. Its seed is the middle
 * segment of every Sofia item id (`a_<seed>_<n>`, see nextId in useCoviChat);
 * swap to a real turn/conversation id if one is added to the item model.
 */
export function CoviMessageFeedback({
  messageId,
  messageLength,
}: {
  messageId: string
  messageLength: number
}) {
  const [voted, setVoted] = useState(false)
  const { trackEvent } = useAnalytics()

  const handleClick = (rating: Rating) => {
    setVoted(true)
    trackEvent(TRACKING_EVENTS.COPILOT.MESSAGE_FEEDBACK, {
      feedback: rating,
      message_id: messageId,
      session_id: messageId.split("_")[1] ?? "",
      message_length: messageLength,
      surface: "covi",
      assistant_brand: "sofia",
    })
    toast.success("Thank you for your feedback!")
  }

  if (voted) {
    return null
  }

  return (
    <div className="mt-1 flex items-center gap-0.5 opacity-0 transition-opacity focus-within:opacity-100 group-hover/covi-msg:opacity-100">
      <Button
        variant="ghost"
        icon
        tooltip="Helpful"
        aria-label="Mark reply as helpful"
        onClick={() => handleClick("positive")}
      >
        <ThumbsUp className="h-3.5 w-3.5" />
      </Button>

      <Button
        variant="ghost"
        icon
        tooltip="Not helpful"
        aria-label="Mark reply as not helpful"
        onClick={() => handleClick("negative")}
      >
        <ThumbsDown className="h-3.5 w-3.5" />
      </Button>
    </div>
  )
}
